import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { teamService } from '../services/api';
import { Card, Input, Textarea, Button, Select, NormalMultiSelect, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const CreateEditTeam = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [categoryType, setCategoryType] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
 
  
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [regionsError, setRegionsError] = useState('');
  const [categoriesError, setCategoriesError] = useState('');
  const [usersError, setUsersError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    if (isViewMode) {
      setPageTitle('View Team');
    } else if (isEditMode) {
      setPageTitle('Edit Team');
    } else {
      setPageTitle('Add Team');
    }
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchTeamData();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchRegions = async (searchTerm = '') => {
    try {
      setLoadingRegions(true);
      const includeCodes = (isEditMode || isViewMode) && selectedRegions.length > 0 ? selectedRegions.join(',') : undefined;
      const response = await teamService.searchRegions(searchTerm, includeCodes);
      setRegions(response.data.map(r => ({ value: r.code, label: r.name })));
    } catch (err) {
      console.error('Failed to load regions:', err);
    } finally {
      setLoadingRegions(false);
    }
  };
  const fetchCategories = async (searchTerm = '', type = '') => {
    try {
      setLoadingCategories(true);
      const includeCodes = (isEditMode || isViewMode) && selectedCategories.length > 0 ? selectedCategories.join(',') : undefined;
      const response = await teamService.searchCategories(searchTerm, type || categoryType, includeCodes);
      setCategories(response.data.map(c => ({ value: c.code, label: c.name })));
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryTypeChange = (e) => {
    const type = e.target.value;
    setCategoryType(type);
    setSelectedCategories([]); // Reset category selection when type changes
    if (type) {
      fetchCategories('', type);
    } else {
      setCategories([]);
    }
  };

  const fetchUsers = async (searchTerm = '') => {
    if (selectedRegions.length === 0) return;
    
    try {
      setLoadingUsers(true);
      const response = await teamService.getUsersForDropdown(selectedRegions, selectedUsers, searchTerm);
      
      // Handle response format - check if validation was done
      const fetchedUsers = response.data.users 
        ? response.data.users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }))
        : response.data.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }));
      
      // Update selected users to only valid ones if validation was performed
      if (response.data.validSelectedUsers) {
        setSelectedUsers(response.data.validSelectedUsers);
        
        // Merge with currently selected users to ensure they're always visible
        const selectedUserObjects = users.filter(u => response.data.validSelectedUsers.includes(u.value));
        const fetchedUserIds = new Set(fetchedUsers.map(u => u.value));
        const selectedNotInFetched = selectedUserObjects.filter(u => !fetchedUserIds.has(u.value));
        
        // Combine: selected users that aren't in search results go first, then search results
        const mergedUsers = [...selectedNotInFetched, ...fetchedUsers];
        setUsers(mergedUsers);
      } else {
        // No validation, just show fetched users
        const selectedUserObjects = users.filter(u => selectedUsers.includes(u.value));
        const fetchedUserIds = new Set(fetchedUsers.map(u => u.value));
        const selectedNotInFetched = selectedUserObjects.filter(u => !fetchedUserIds.has(u.value));
        const mergedUsers = [...selectedNotInFetched, ...fetchedUsers];
        setUsers(mergedUsers);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const response = await teamService.getById(id);
      const team = response.data;
      
      setName(team.name);
      setDescription(team.description || '');
      setSelectedRegions(team.regions || []);
      setSelectedUsers(team.users || []);
      
      // Handle categories and determine category type
      if (team.categories && team.categories.length > 0) {
        // Extract category codes for selection
        const categoryCodes = team.categories.map(c => c.code);
        setSelectedCategories(categoryCodes);
        
        // Determine category type from the first category (assuming all categories have the same type)
        const categoryType = team.categories[0]?.category_type;
        if (categoryType) {
          setCategoryType(categoryType);
          
          // Fetch categories of this type (include currently mapped codes even if deleted/inactive)
          try {
            const includeCatCodes = categoryCodes.length > 0 ? categoryCodes.join(',') : undefined;
            const categoriesResponse = await teamService.searchCategories('', categoryType, includeCatCodes);
            setCategories(categoriesResponse.data.map(c => ({ value: c.code, label: c.name })));
          } catch (err) {
            console.error('Failed to load categories for team:', err);
          }
        }
      } else {
        setSelectedCategories([]);
      }
      
      // Fetch dropdown data with selected values to prefill
      // Fetch regions (include currently mapped region codes even if deleted/inactive)
      try {
        const includeCodes = team.regions && team.regions.length > 0 ? team.regions.join(',') : undefined;
        const regionsResponse = await teamService.searchRegions('', includeCodes);
        setRegions(regionsResponse.data.map(r => ({ value: r.code, label: r.name })));
      } catch (err) {
        console.error('Failed to load regions for team:', err);
      }

      // Fetch users for the selected regions
      if (team.regions && team.regions.length > 0) {
        try {
          const usersResponse = await teamService.getUsersForDropdown(team.regions, team.users || [], '');
          
          // Handle response format - check if validation was done
          const fetchedUsers = usersResponse.data.users 
            ? usersResponse.data.users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }))
            : usersResponse.data.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }));
          
          setUsers(fetchedUsers);
          
          // Update selected users to only valid ones if validation was performed
          if (usersResponse.data.validSelectedUsers) {
            setSelectedUsers(usersResponse.data.validSelectedUsers);
          }
        } catch (err) {
          console.error('Failed to load users for team:', err);
        }
      }
    } catch (err) {
      toast.error('Failed to fetch team details');
      console.error(err);
      navigate('/admin/teams');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let isValid = true;
    setNameError('');
    setDescriptionError('');
    setRegionsError('');
    setCategoriesError('');
    setUsersError('');
    setError('');

    if (!name.trim()) {
      setNameError('Team name is required');
      isValid = false;
    }

    if (!description.trim()) {
      setDescriptionError('Team description is required');
      isValid = false;
    }

    if (selectedRegions.length === 0) {
      setRegionsError('At least one region must be selected');
      isValid = false;
    }

    if (!categoryType) {
      setCategoriesError('Category type must be selected');
      isValid = false;
    }

    if (selectedCategories.length === 0) {
      setCategoriesError('At least one category must be selected');
      isValid = false;
    }

    if (selectedUsers.length === 0) {
      setUsersError('At least one user must be selected');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        regions: selectedRegions,
        categories: selectedCategories,
        users: selectedUsers,
      };

      if (isEditMode) {
        await teamService.update(id, data);
        toast.success('Team updated successfully');
      } else {
        await teamService.create(data);
        toast.success('Team created successfully');
      }
      
      navigate('/admin/teams');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} team`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/teams');
  };

  if (loading && (isEditMode || isViewMode) && !name) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading team details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: 'Teams', to: '/admin/teams' },
              { label: isViewMode ? 'View Team' : isEditMode ? 'Edit Team' : 'Add Team' }
            ]}
            className="mb-6"
          />

          {/* Team Name */}
          <div className="mb-6">
            <Input
              label="Team Name"
              name="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              placeholder="Enter team name"
              required
              error={nameError}
              disabled={loading || isViewMode}
            />
          </div>

          {/* Team Description */}
          <div className="mb-6">
            <Textarea
              label="Description"
              name="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (descriptionError) setDescriptionError('');
              }}
              placeholder="Enter team description"
              rows={3}
              required
              error={descriptionError}
              disabled={loading || isViewMode}
            />
          </div>

          {/* Regions Multi-Select */}
          <div className="mb-6">
            <NormalMultiSelect
              label="Regions"
              name="regions"
              value={selectedRegions}
              onChange={async (e) => {
                const newRegions = e.target.value;
                setSelectedRegions(newRegions);
                if (regionsError) setRegionsError('');
              
                // If all regions removed, clear everything
                if (newRegions.length === 0) {
                  setSelectedUsers([]);
                  setUsers([]);
                } else {
                  // Validate and fetch users for new region selection
                  try {
                    setLoadingUsers(true);
                    const response = await teamService.getUsersForDropdown(newRegions, selectedUsers, '');
                    const usersList = response.data.users || response.data || [];
                    const fetchedUsers = usersList.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }));

                    // Keep only users who still belong to one of the remaining regions.
                    // Users tied solely to the removed region are dropped here.
                    const validSelected = response.data.validSelectedUsers || [];
                    setSelectedUsers(validSelected);

                    // Merge selected valid users with fetched users
                    const selectedUserObjects = fetchedUsers.filter(u => validSelected.includes(u.value));
                    const fetchedUserIds = new Set(fetchedUsers.map(u => u.value));
                    const selectedNotInFetched = selectedUserObjects.filter(u => !fetchedUserIds.has(u.value));
                    const mergedUsers = [...selectedNotInFetched, ...fetchedUsers];
                    setUsers(mergedUsers);
                  } catch (err) {
                    console.error('Failed to load users:', err);
                  } finally {
                    setLoadingUsers(false);
                  }
                }
              }}
              options={regions}
              placeholder="Select regions"
              required
              error={regionsError}
              disabled={loading || isViewMode}
              onOpen={fetchRegions}
              loading={loadingRegions}
              onSearch={fetchRegions}
            />
          </div>

          {/* Category Type Dropdown */}
          <div className="mb-6">
            <Select
              label="Category Type"
              name="categoryType"
              value={categoryType}
              onChange={handleCategoryTypeChange}
              required
              disabled={loading || isViewMode}
            >
              <option value="">Select category type</option>
              <option value="Software">Software</option>
              <option value="Hardware">Hardware</option>
            </Select>
          </div>

          {/* Categories Multi-Select */}
          <div className="mb-6">
            <NormalMultiSelect
              label="Categories"
              name="categories"
              value={selectedCategories}
              onChange={(e) => {
                setSelectedCategories(e.target.value);
                if (categoriesError) setCategoriesError('');
              }}
              options={categories}
              placeholder={!categoryType ? "Select category type first" : "Select categories"}
              required
              error={categoriesError}
              disabled={loading || isViewMode || !categoryType}
              onOpen={() => categoryType && fetchCategories()}
              loading={loadingCategories}
              onSearch={(searchTerm) => categoryType && fetchCategories(searchTerm)}
            />
          </div>

          {/* Users Multi-Select */}
          <div className="mb-6">
            <NormalMultiSelect
              label="Users"
              name="users"
              value={selectedUsers}
              onChange={(e) => {
                setSelectedUsers(e.target.value);
                if (usersError) setUsersError('');
              }}
              options={users}
              placeholder={selectedRegions.length === 0 ? "Select regions first" : "Select users"}
              required
              error={usersError}
              disabled={loading || isViewMode || selectedRegions.length === 0}
              onOpen={() => selectedRegions.length > 0 && fetchUsers()}
              loading={loadingUsers}
              onSearch={fetchUsers}
            />
          </div>

          {/* Actions Row: Buttons at far right */}
          {!isViewMode && (
            <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Team' : '+ Add Team'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CreateEditTeam;
